import { useState } from 'react';

import { getRoleColor } from './roleColors';
import { ScopeEditor } from './ScopeEditor';
import {
  EXTRANET_ROLES,
  isKnownRole,
  ROLE_LABELS,
  roleDescription,
  scopeSummary,
  type ExtranetRole,
  type RoleGrant,
  type RoleScope,
} from '../../domain/access';
import { IconChevronDown } from '../incidents/icons';

interface AccessEditorProps {
  value: RoleGrant[];
  onChange: (next: RoleGrant[]) => void;
  disabled?: boolean;
  /** Rôles qu'on ne peut pas décocher (ex. son propre rôle Administrateur). */
  lockedRoles?: ExtranetRole[];
  idPrefix: string;
}

/**
 * Éditeur d'un jeu d'accès `RoleGrant[]` : une case par profil, et pour chaque
 * profil coché un périmètre optionnel. Les rôles inconnus du front sont
 * conservés tels quels dans la valeur. Partagé par la modale d'accès d'un
 * membre et le formulaire d'invitation.
 */
export const AccessEditor = ({ value, onChange, disabled, lockedRoles = [], idPrefix }: AccessEditorProps) => {
  const grantFor = (role: ExtranetRole) => value.find(grant => grant.role === role);

  const toggleRole = (role: ExtranetRole, checked: boolean) => {
    onChange(checked ? [...value, { role, scopes: [] }] : value.filter(grant => grant.role !== role));
  };

  const setScopes = (role: ExtranetRole, scopes: RoleScope[]) => {
    onChange(value.map(grant => (grant.role === role ? { ...grant, scopes } : grant)));
  };

  const unknownRoles = value.filter(grant => !isKnownRole(grant.role)).map(grant => grant.role);

  return (
    <div className="flex flex-col gap-2">
      {EXTRANET_ROLES.map(role => {
        const grant = grantFor(role);
        const checked = Boolean(grant);
        const locked = lockedRoles.includes(role);
        const tokens = getRoleColor(role);
        const inputId = `${idPrefix}-role-${role}`;
        return (
          <div key={role} className={['rounded-md border', checked ? 'border-zinc-300' : 'border-zinc-200'].join(' ')}>
            <label htmlFor={inputId} className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer">
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                disabled={disabled || locked}
                onChange={e => toggleRole(role, e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-zinc-900"
              />
              <span className="flex flex-col gap-0.5 min-w-0">
                <span
                  className={`self-start rounded-md text-[10px] font-medium px-1.5 py-0.5 ${tokens.bg} ${tokens.text}`}
                >
                  {ROLE_LABELS[role]}
                </span>
                <span className="text-[11px] text-zinc-500">{roleDescription(role)}</span>
                {locked ? (
                  <span className="text-[11px] text-zinc-500 italic">
                    Vous ne pouvez pas retirer votre propre rôle Administrateur.
                  </span>
                ) : null}
              </span>
            </label>
            {grant ? (
              <RoleScopeSection
                grant={grant}
                onChange={scopes => setScopes(role, scopes)}
                disabled={disabled}
                idPrefix={`${idPrefix}-${role}`}
              />
            ) : null}
          </div>
        );
      })}

      {unknownRoles.length > 0 ? (
        <p className="text-[11px] text-zinc-500">
          Autres rôles conservés à l&apos;enregistrement : {unknownRoles.join(', ')}
        </p>
      ) : null}
    </div>
  );
};

const RoleScopeSection = ({
  grant,
  onChange,
  disabled,
  idPrefix,
}: {
  grant: RoleGrant;
  onChange: (scopes: RoleScope[]) => void;
  disabled?: boolean;
  idPrefix: string;
}) => {
  const [open, setOpen] = useState(grant.scopes.some(scope => scope.values.length > 0));

  return (
    <div className="border-t border-zinc-100 px-3 py-2 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="self-start inline-flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-900 transition-colors"
      >
        <IconChevronDown width={12} height={12} className={open ? '' : '-rotate-90'} />
        <span>{open ? 'Restreindre le périmètre' : `Périmètre : ${scopeSummary(grant)}`}</span>
      </button>
      {open ? <ScopeEditor value={grant.scopes} onChange={onChange} disabled={disabled} idPrefix={idPrefix} /> : null}
    </div>
  );
};
